import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { useToast } from '../components/ui/use-toast';
import { AgeDeclaration } from '../components/auth/age-declaration';
import { SocialSignIn } from '../components/auth/social-sign-in';
import AthleteFields from '../components/guardian/AthleteFields';
import AthleteEntryOptions, { type AthleteEntryStatus } from '../components/guardian/AthleteEntryOptions';
import {
  athleteIsComplete,
  emptyAthlete,
  type AthleteDraft,
} from '../components/guardian/athlete-draft';
import GuardianDeclaration from '../components/guardian/GuardianDeclaration';
import { choosesTurn, chosenTurn } from '../components/trophy/entry-options';
import { useAuth } from '../hooks/useAuth';
import { competitionsService, type GuardianSignUp, type SignUpAthlete } from '../services/competitions';
import { errorCodeOf } from '../services/api-error';
import { trackEvent } from '../utils/analytics';
import i18n from '../i18n';

/** The refusals about one child's entry, worded as the entry dialog words them. */
const ENTRY_CODES = new Set(['gender_required', 'round_required', 'round_not_offered']);

/**
 * Signing up to enter a child: the athletes first, the parent second.
 *
 * The order is the point. The ordinary sign-up asks for a username and a
 * password and then, somewhere later, who is climbing — which is the wrong
 * question for a parent, because the person climbing is nine and will never
 * have a username. So this page starts with the children (name, surname, date
 * of birth, and the colour their avatar gets), lets the parent add as many as
 * they have, and only then asks who *they* are.
 *
 * One request creates all of it — parent, children and entries — because any
 * half of it is a state nobody could sort out afterwards (see
 * Api::Competitions::SignUpsController). Google is the same request with a
 * credential in place of a password.
 *
 * Each child is entered at once, so each is asked what the entry dialog would
 * ask: the gender of the class they compete in (required, kept on their
 * account), and then — shown under their details — the category and rounds
 * that leads to, with the turn to book when the class has two or more. A child
 * counts as ready only with all of it; the button says how many are.
 *
 * It is always about one competition: `?stage=` is where the parent came from,
 * and where they are sent back to with everybody entered.
 */
export default function GuardianSignUpPage() {
  const [searchParams] = useSearchParams();
  const stageSlug = searchParams.get('stage') ?? '';
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { adoptSession, isAuthenticated } = useAuth();

  const state = location.state as { from?: string; stageName?: string } | null;
  const [stageName, setStageName] = useState(state?.stageName ?? '');

  const [athletes, setAthletes] = useState<AthleteDraft[]>([emptyAthlete()]);
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [declared, setDeclared] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // A refresh loses what the dialog handed over, so the stage is named from
  // its own record rather than only from the navigation state.
  useEffect(() => {
    if (!stageSlug || stageName) return;
    competitionsService
      .getCompetitionBySlug(stageSlug)
      .then((competition) => setStageName(competition.name))
      .catch(() => undefined);
  }, [stageSlug, stageName]);

  // Each child's preview, as its box last reported it: the page holds the
  // submit back while one is loading or still has a turn to pick.
  const [entryStatus, setEntryStatus] = useState<Record<string, AthleteEntryStatus>>({});
  const reportStatus = useCallback((key: string, status: AthleteEntryStatus) => {
    setEntryStatus((current) => ({ ...current, [key]: status }));
  }, []);

  /** The turn this child books: only when their class has turns to choose from. */
  const turnOf = useCallback(
    (athlete: AthleteDraft) => {
      const options = entryStatus[athlete.key]?.options ?? null;
      return choosesTurn(options) ? chosenTurn(options, athlete.competition_round_id ?? null) : null;
    },
    [entryStatus]
  );

  const ready = useMemo(
    () =>
      athletes.filter((athlete) => {
        if (!athleteIsComplete(athlete) || !athlete.gender) return false;
        const status = entryStatus[athlete.key];
        if (!status || status.loading) return false;
        return !choosesTurn(status.options) || !!turnOf(athlete);
      }),
    [athletes, entryStatus, turnOf]
  );
  const clearError = () => { if (formError) setFormError(null); };

  // Nothing to enter: whoever landed here by hand belongs on the plain form.
  if (!stageSlug) return <Navigate to="/sign-up" replace />;
  // Already signed in, so the children are added from the picker on the stage
  // instead — this page can only make a new account.
  if (isAuthenticated) return <Navigate to={state?.from ?? `/competitions/${stageSlug}`} replace />;

  const setAthlete = (index: number, next: AthleteDraft) =>
    setAthletes((current) => current.map((athlete, at) => (at === index ? next : athlete)));

  const payloadAthletes = (): SignUpAthlete[] =>
    ready.map((athlete) => {
      const turn = turnOf(athlete);
      return {
        name: athlete.name.trim(),
        surname: athlete.surname.trim(),
        birth_date: athlete.birth_date,
        avatar_color: athlete.avatar_color,
        gender: athlete.gender as 'male' | 'female',
        ...(turn ? { competition_round_id: turn } : {}),
      };
    });

  const submit = async (credentials: Pick<GuardianSignUp, 'guardian' | 'credential'>) => {
    if (ready.length === 0 || !ageConfirmed || !declared) return;

    setFormError(null);
    setIsLoading(true);
    try {
      const result = await competitionsService.signUpAndEnter(stageSlug, {
        athletes: payloadAthletes(),
        locale: i18n.language,
        ...credentials,
      });

      adoptSession(result);
      trackEvent('sign_up');
      toast({
        title: t('guardian.signUp.done', {
          names: result.athletes.map((athlete) => athlete.full_name).join(', '),
          stage: stageName,
        }),
      });
      navigate(state?.from ?? `/competitions/${stageSlug}`);
    } catch (error) {
      // A child's gender or turn refused (a turn added since the page read
      // them): said in the entry dialog's words; anything else in the server's.
      const code = errorCodeOf(error);
      const message = error instanceof Error ? error.message : t('errors.unexpectedError');
      setFormError(code && ENTRY_CODES.has(code) ? t(`stageEntry.dialog.errors.${code}`) : message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== passwordConfirmation) {
      setFormError(t('auth.passwordsDontMatch'));
      return;
    }
    if (!/^[a-z0-9_-]+$/.test(username)) {
      setFormError(t('invitation.usernameFormatError'));
      return;
    }

    submit({
      guardian: {
        name,
        surname,
        username,
        email,
        password,
        password_confirmation: passwordConfirmation,
        language: i18n.language,
      },
    });
  };

  // The guardian's half stays shut until there is somebody to enter: the page
  // is about the children, and asking a parent for a password before anybody
  // is named is how the ordinary form gets this wrong.
  const guardianUnlocked = ready.length > 0;

  return (
    <div className="my-8 flex justify-center">
      <Card className="w-full max-w-xl">
        <CardHeader>
          <CardTitle>{t('guardian.signUp.title')}</CardTitle>
          <CardDescription>
            {stageName
              ? t('guardian.signUp.descriptionFor', { stage: stageName })
              : t('guardian.signUp.description')}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6">
            <section className="space-y-4" data-testid="athletes-step">
              <div>
                <h3 className="text-sm font-semibold">{t('guardian.signUp.athletesTitle')}</h3>
                <p className="text-xs text-muted-foreground">{t('guardian.signUp.athletesHint')}</p>
              </div>

              {athletes.map((athlete, index) => (
                <div key={athlete.key} className="space-y-3 rounded-lg border p-3" data-testid="sign-up-athlete">
                  <AthleteFields
                    value={athlete}
                    onChange={(next) => { setAthlete(index, next); clearError(); }}
                    idPrefix={`athlete-${index}`}
                    withGender
                    genderRequired
                    onRemove={
                      athletes.length > 1
                        ? () => setAthletes((current) => current.filter((_, at) => at !== index))
                        : undefined
                    }
                  />
                  <AthleteEntryOptions
                    stageSlug={stageSlug}
                    athlete={athlete}
                    onRoundChange={(roundId) => {
                      setAthlete(index, { ...athlete, competition_round_id: roundId });
                      clearError();
                    }}
                    onStatus={reportStatus}
                  />
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setAthletes((current) => [...current, emptyAthlete()])}
                data-testid="add-another-athlete"
              >
                <Plus className="size-4" aria-hidden />
                {t('guardian.signUp.addAnother')}
              </Button>
            </section>

            <Separator />

            <section
              className={guardianUnlocked ? 'space-y-4' : 'space-y-4 opacity-50'}
              aria-disabled={!guardianUnlocked}
              data-testid="guardian-step"
            >
              <div>
                <h3 className="text-sm font-semibold">{t('guardian.signUp.guardianTitle')}</h3>
                <p className="text-xs text-muted-foreground">
                  {guardianUnlocked
                    ? t('guardian.signUp.guardianHint')
                    : t('guardian.signUp.guardianLocked')}
                </p>
              </div>

              {guardianUnlocked && (
                <>
                  <AgeDeclaration
                    checked={ageConfirmed}
                    onCheckedChange={(checked) => { setAgeConfirmed(checked); clearError(); }}
                  />
                  <GuardianDeclaration
                    id="guardian-signup-declaration"
                    checked={declared}
                    onCheckedChange={(checked) => { setDeclared(checked); clearError(); }}
                  />

                  {/* Google's button is an iframe we cannot disable, so make it
                      unreachable until both declarations are given. */}
                  <div
                    data-testid="social-sign-in"
                    aria-disabled={!ageConfirmed || !declared}
                    className={ageConfirmed && declared ? undefined : 'pointer-events-none opacity-50'}
                  >
                    <SocialSignIn
                      onGoogleCredential={(credential) => submit({ credential })}
                      googleText="signup_with"
                    />
                  </div>

                  {formError && (
                    <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                      {formError}
                    </div>
                  )}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="guardian-name">{t('profile.name')}</Label>
                      <Input
                        id="guardian-name"
                        value={name}
                        onChange={(e) => { setName(e.target.value); clearError(); }}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guardian-surname">{t('profile.surname')}</Label>
                      <Input
                        id="guardian-surname"
                        value={surname}
                        onChange={(e) => { setSurname(e.target.value); clearError(); }}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guardian-username">{t('profile.username')}</Label>
                    <Input
                      id="guardian-username"
                      value={username}
                      onChange={(e) => { setUsername(e.target.value.toLowerCase()); clearError(); }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guardian-email">{t('auth.email')}</Label>
                    <Input
                      id="guardian-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); clearError(); }}
                      required
                    />
                    <p className="text-xs text-muted-foreground">{t('guardian.signUp.emailHint')}</p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="guardian-password">{t('auth.password')}</Label>
                      <Input
                        id="guardian-password"
                        type="password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); clearError(); }}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guardian-password-confirmation">{t('auth.confirmPassword')}</Label>
                      <Input
                        id="guardian-password-confirmation"
                        type="password"
                        value={passwordConfirmation}
                        onChange={(e) => { setPasswordConfirmation(e.target.value); clearError(); }}
                        required
                      />
                    </div>
                  </div>
                </>
              )}
            </section>
          </CardContent>

          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !guardianUnlocked || !ageConfirmed || !declared}
            >
              {isLoading && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('guardian.signUp.submit', { count: ready.length })}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              {t('auth.alreadyHaveAccount')}{' '}
              <Link
                to="/sign-in"
                state={{ from: state?.from ?? `/competitions/${stageSlug}` }}
                className="text-primary hover:underline"
              >
                {t('auth.signInNow')}
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
